import { RefObject, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/** Tabbable descendants of `container`, in DOM order, skipping anything hidden. */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    // Not a layout check (getClientRects) — jsdom has no layout, so that would empty the list in tests.
    (el) => !el.closest('[hidden], [aria-hidden="true"]'),
  );
}

interface FocusTrapOptions {
  /** Called on Escape, unless something inside already handled it (preventDefault). */
  onEscape?: () => void;
  /** Element to focus on open. Defaults to the first tabbable element, else the container. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Selector for the element to focus on open, used when `initialFocusRef` isn't given. */
  initialFocusSelector?: string;
  /** Return focus to whatever was focused before opening. Defaults to true. */
  restoreFocus?: boolean;
}

interface ActiveTrap {
  containerRef: RefObject<HTMLElement | null>;
  onEscapeRef: { current: (() => void) | undefined };
}

// Open traps, oldest first. Only the last (topmost) one handles keys: with one
// document listener per trap, Escape fired every stacked dialog's handler in
// registration order — so the dialog *underneath* closed too.
const trapStack: ActiveTrap[] = [];

function handleTrapKeyDown(event: KeyboardEvent): void {
  const top = trapStack[trapStack.length - 1];
  const container = top?.containerRef.current;
  if (!top || !container) return;

  if (event.key === 'Escape') {
    // An inner widget (react-select menu, listbox) already used this Escape.
    if (event.defaultPrevented || !top.onEscapeRef.current) return;
    // Consume it: preventDefault for listeners that check defaultPrevented, and
    // stopImmediatePropagation so no other document listener (e.g. the
    // useEscapeKey of a form behind a "Discard changes?" confirm) also acts on it.
    event.preventDefault();
    event.stopImmediatePropagation();
    top.onEscapeRef.current();
    return;
  }

  if (event.key !== 'Tab') return;
  const focusable = getFocusableElements(container);
  if (focusable.length === 0) {
    event.preventDefault();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const current = document.activeElement;
  if (event.shiftKey && (current === first || !container.contains(current))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (current === last || !container.contains(current))) {
    event.preventDefault();
    first.focus();
  }
}

// Registered once, at module load — before any component effect adds its own
// document keydown listener — so it runs ahead of them and stopImmediatePropagation
// can actually shield them. Still a bubble-phase listener, so React handlers inside
// the dialog (which run at the root container, below document) see the key first.
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', handleTrapKeyDown);
}

/**
 * Modal focus management (WCAG 2.1.2 / 2.4.3): while `active`, moves focus into
 * `containerRef`, keeps Tab / Shift+Tab cycling inside it, closes on Escape, and on
 * deactivation returns focus to the element that opened it. When dialogs stack, only
 * the topmost trap responds to keys.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  { onEscape, initialFocusRef, initialFocusSelector, restoreFocus = true }: FocusTrapOptions = {},
): void {
  // Kept in a ref so a new onEscape closure each render doesn't re-run the effect,
  // which would re-steal focus and lose the element to restore.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const entry: ActiveTrap = { containerRef, onEscapeRef };
    trapStack.push(entry);

    // Deferred a frame: portaled / freshly mounted content may not be laid out yet.
    const frame = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container || container.contains(document.activeElement)) return;
      const preferred =
        initialFocusRef?.current ??
        (initialFocusSelector ? container.querySelector<HTMLElement>(initialFocusSelector) : null);
      const target = preferred ?? getFocusableElements(container)[0] ?? container;
      if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      target.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      // By identity, not pop(): traps can close out of order.
      const index = trapStack.indexOf(entry);
      if (index !== -1) trapStack.splice(index, 1);
      if (restoreFocus && previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
    // Only `active`: the refs and options are read when the trap opens, and re-running on
    // their change would re-steal focus and lose the element to restore.
  }, [active]);
}
